import Placeholder from "@tiptap/extension-placeholder";
import { uploadFile } from "../../../revampCRUD/uploads.crud";
import { insertOptions } from "../../TipTapEditor/config/constants";
import {
	BaseKit,
	Blockquote,
	Bold,
	BulletList,
	Clear,
	Color,
	ColumnActionButton,
	ExportPdf,
	FontSize,
	Heading,
	Highlight,
	History,
	HorizontalRule,
	Image,
	ImageUpload,
	Italic,
	LineHeight,
	Link,
	MoreMark,
	OrderedList,
	SearchAndReplace,
	Strike,
	Table,
	TextAlign,
	Underline,
	InsertVariable,
	CustomDiv,
	Video,
	VideoUpload,
	ImportWord,
} from "../extensions";
import { HTMLEditorExtension } from "../extensions/HtmlEditor/HtmlEditor";
import { CustomInline } from "../extensions/CustomDiv/CustomInline";

export const getTipTapExtensions = ({
	content,
	onChangeContent,
	placeholder,
	showInsertVariables = false,
	isHtmlEditable = false,
	showHistory = false,
	showFindReplace = false,
	showTextType = false,
	showFontSize = false,
	showTextFormatters = false,
	showTextHighlighters = false,
	showLists = false,
	showTextAligners = false,
	showLink = false,
	showImage = false,
	showVideo = false,
	showBlockQuote = false,
	showHr = false,
	showTable = false,
	showColumnLayout = false,
	isExportable = false,
	showImportDoc = false,
	showAllOptions = false
}) => {
	const toolbarFeatures = [
		BaseKit.configure({
			characterCount: {
				limit: 50_000
			},
			innerHeight: 140,
		}),
		InsertVariable.configure({
			variablesList: insertOptions
		}),
		HTMLEditorExtension.configure({ content: content, setContent: onChangeContent }),
		Clear.configure({ spacer: true }),
		History,
		SearchAndReplace,
		Heading.configure({ spacer: true }),
		FontSize,
		Bold,
		Italic,
		Underline,
		Strike,
		Color.configure({ spacer: true }),
		MoreMark,
		Highlight,
		BulletList,
		OrderedList,
		TextAlign.configure({ types: ["heading", "paragraph"], spacer: true }),
		LineHeight,
		Link,
		Image,
		ImageUpload.configure({
			upload: async file => {
				try {
					const downloadUrl = await uploadFile(file, 4);
					return downloadUrl;
				} catch (error) {
					console.error("Upload failed:", error);
					throw error;
				}
			}
		}),
		Video,
		VideoUpload,
		Blockquote,
		HorizontalRule,
		ColumnActionButton,
		ExportPdf.configure({ spacer: true }),
		ImportWord.configure({
			upload: files => {
				const fileData = files.map(file => ({
					src: URL.createObjectURL(file),
					alt: file.name
				}));
				return Promise.resolve(fileData);
			}
		}),
		CustomDiv,
		CustomInline,
	];

	if (showAllOptions) {
		return toolbarFeatures;
	}

	const basicToolbarFeatures = [
		BaseKit.configure({
			characterCount: {
				limit: 50_000
			}
		}),
	];

	if (isHtmlEditable) {
		basicToolbarFeatures.push(HTMLEditorExtension.configure({ content: content, setContent: onChangeContent }));
	}

	basicToolbarFeatures.push(Clear, Image, Video);

	const preparedFeatureList = [];

	if (showInsertVariables) {
		preparedFeatureList.push(
			InsertVariable.configure({
				variablesList: insertOptions
			})
		);
	}
	if (showHistory) {
		preparedFeatureList.push(History);
	}
	if (showFindReplace) {
		preparedFeatureList.push(SearchAndReplace);
	}
	if (showTextFormatters) {
		preparedFeatureList.push(Bold, Italic, Underline, Strike);
	}
	if (showTextHighlighters) {
		preparedFeatureList.push(Color.configure({ spacer: true }), MoreMark, Highlight);
	}
	if (showTextType) {
		preparedFeatureList.push(Heading.configure({ spacer: true }));
	}
	if (showFontSize) {
		preparedFeatureList.push(FontSize);
	}
	if (showLists) {
		preparedFeatureList.push(BulletList, OrderedList);
	}
	if (showTextAligners) {
		preparedFeatureList.push(
			TextAlign.configure({ types: ["heading", "paragraph"], spacer: true }),
			LineHeight
		);
	}
	if (showLink) {
		preparedFeatureList.push(Link);
	}
	if (showImage) {
		preparedFeatureList.push(
			ImageUpload.configure({
				upload: async file => {
					try {
						const downloadUrl = await uploadFile(file, 4);
						return downloadUrl;
					} catch (error) {
						console.error("Upload failed:", error);
						throw error;
					}
				}
			})
		);
	}
	if (showVideo) {
		preparedFeatureList.push(VideoUpload);
	}
	if (showBlockQuote) {
		preparedFeatureList.push(Blockquote);
	}
	if (showHr) {
		preparedFeatureList.push(HorizontalRule);
	}
	if (showTable) {
		preparedFeatureList.push(Table);
	}
	if (showColumnLayout) {
		preparedFeatureList.push(ColumnActionButton);
	}
	if (isExportable) {
		preparedFeatureList.push(ExportPdf.configure({ spacer: true }));
	}
	if (showImportDoc) {
		preparedFeatureList.push(
			ImportWord.configure({
				upload: files => {
					const fileData = files.map(file => ({
						src: URL.createObjectURL(file),
						alt: file.name
					}));
					return Promise.resolve(fileData);
				}
			})
		);
	}

	return [
		Placeholder.configure({
			placeholder: ({ editor }) => {
				if (editor.isEmpty) {
					return placeholder;
				}
				return '';
			},
		}),
		...new Set([...basicToolbarFeatures, ...preparedFeatureList]),
		CustomDiv, CustomInline];
};
